/**
 * 공통 유틸 함수 컴포저블
 * 목적: 공통 유틸리티 함수 제공
 */

/****************************************************
 * 유틸리티 함수들
 ****************************************************/

// === 개별 export 함수들 ===

/** 숫자 포맷 (콤마 추가) — 큰 정수도 정밀도 유지 */
export const addComma = (val: number | string | null | undefined): string => {
    if (val == null || val === '') return ''
    const str = String(val).trim().replace(/,/g, '')
    if (str === '') return ''
    // 숫자 형식이 아니면 빈 문자열
    if (!/^-?\d+(\.\d+)?$/.test(str)) {
        const num = Number(str)
        return isNaN(num) ? '' : num.toLocaleString()
    }
    // 안전 정수 범위 내이면 기존 방식
    const num = Number(str)
    if (Number.isSafeInteger(num) && !str.includes('.')) {
        return num.toLocaleString()
    }
    if (!isNaN(num) && Math.abs(num) <= Number.MAX_SAFE_INTEGER) {
        return num.toLocaleString()
    }
    // 큰 정수: 문자열 기반 콤마 삽입
    const isNegative = str.startsWith('-')
    const absolute = isNegative ? str.slice(1) : str
    const parts = absolute.split('.')
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    const result = parts.length > 1 ? `${intPart}.${parts[1]}` : intPart
    return isNegative ? `-${result}` : result
}

/** 콤마 제거 */
export const removeComma = (val: string): string => {
    if (isEmpty(val)) return ''
    return val.toString().replace(/,/g, '')
}

/** 숫자 입력 제한 (음수, 소수점 포함) */
export const numeric = (obj: HTMLInputElement, event: Event): string => {
    const text = obj.value
    const reg = /(^[-])?([0-9]*[.]?[0-9]*)/g
    const res = text.match(reg)
    const result = res ? res[0] : ''
    obj.value = result
    return result
}

/** 확장 숫자 입력 제한 */
export const numericExt = (
    obj: HTMLInputElement,
    event: Event,
    negative: boolean = false,
    intLang?: number,
    point?: number
): string => {
    let val = (event.target as HTMLInputElement).value

    if (negative) {
        val = val.replace('-', '')
    }

    const intLangTxt = intLang ? `{1,${intLang}}` : '*'
    const pointTxt = point ? `{0,${point}}` : '*'
    const regTxt = `^-?\\d${intLangTxt}(\\.\\d${pointTxt})?`
    const pattern = new RegExp(regTxt, 'g')
    const res = val.match(pattern)
    const result = res && res.length > 0 ? res[0] : ''

    ;(event.target as HTMLInputElement).value = result
    obj.value = result
    return result
}

/** undefined 여부 확인 */
export const isUndefined = (val: any): boolean => typeof val === 'undefined'

/** null 여부 확인 */
export const isNull = (val: any): boolean => val === null

/** 값이 비어있는지 확인 */
export const isEmpty = (val: any): boolean => {
    return (
        typeof val === 'undefined' ||
        val === null ||
        (typeof val === 'string' && val.trim() === '') ||
        (Array.isArray(val) && val.length === 0)
    )
}

/** 값이 비어있지 않은지 확인 */
export const isNotEmpty = (val: any): boolean => !isEmpty(val)

/** 문자열 길이가 1 이상이면 true (공백만 있어도 true). null/undefined/'' 는 false */
export const hasStringLength = (val: unknown): boolean => typeof val === 'string' && val.length > 0

/** null 값을 빈 문자열로 변환 */
export const removeNull = (val: any): string => (isEmpty(val) ? '' : val)

/** 빈 값을 빈 문자열로 변환 */
export const removeEmpty = (val: any): string => (isEmpty(val) ? '' : val)

/** null/undefined 체크 후 기본값 반환 */
export const nvl = (val: any, altVal: any): any => (isEmpty(val) ? altVal : val)

/** 줄바꿈 문자를 <br/> 태그로 변경 */
export const nl2br = (str: string): string =>
    removeNull(str).replace(/\n/g, '<br/>')

/** 이메일 형식 검증 */
export const checkEmail = (email: string): boolean => {
    const emailRegex =
        /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/
    return emailRegex.test(email)
}

/** 쿠키 생성 */
export const createCookie = (
    name: string,
    value: string,
    days: number,
    secure: boolean = false
): void => {
    let expires = ''
    if (days) {
        const date = new Date()
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
        expires = '; expires=' + date.toUTCString()
    }
    const secureFlag = secure ? '; secure' : ''
    document.cookie = name + '=' + value + expires + '; path=/' + secureFlag
}

/** 쿠키 읽기 */
export const getCookie = (name: string): string | null => {
    const nameEQ = name + '='
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i]
        while (c.charAt(0) === ' ') c = c.substring(1, c.length)
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
    }
    return null
}

/** 쿠키 삭제 */
export const deleteCookie = (name: string): void => {
  createCookie(name, '', -1);
};

/** 쿠키 설정 */
export const setCookie = (
    cookieNm: string,
    value: any,
    expiredDays: number
): void => {
    const now = new Date()
    now.setDate(now.getDate() + expiredDays)
    document.cookie =
        cookieNm + '=' + nvl(value, '') + ';expires=' + now.toUTCString()
}

/** 줄바꿈을 <br> 태그로 변환 */
export const makeBr = (content: string): string =>
  content
    .replace(/(?:\r\n|\r|\n)/g, '<br />')
    .split('\n')
    .join('<br />');

/**
 * HTML 내 텍스트 노드에만 있는 http(s) / www URL을 <a>로 감싼다.
 * (Quill·평문 저장본, TinyMCE autolink 미적용 조회용 — 텍스트 노드만 순회)
 * 기존 <a href> 안·script/style/textarea/pre/code 안은 건드리지 않음.
 */
export const linkifyPlainTextUrlsInHtml = (html: string): string => {
  if (isEmpty(html)) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html')
  const root = doc.body.firstElementChild
  if (!root) return ''

  const pattern = /(https?:\/\/|www\.)\S+/gi
  const normalizeHref = (s: string) => {
    if (/^www\./i.test(s)) return 'https://' + s
    return s
  }

  const walk = (container: Node) => {
    const nodes = Array.from(container.childNodes)
    for (const node of nodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue
        if (!text) continue

        let p: HTMLElement | null = node.parentElement
        let inAnchor = false
        while (p) {
          if (p.tagName.toLowerCase() === 'a' && p.getAttribute('href')) {
            inAnchor = true
            break
          }
          p = p.parentElement
        }
        if (inAnchor) continue

        pattern.lastIndex = 0
        if (!pattern.test(text)) {
          pattern.lastIndex = 0
          continue
        }
        pattern.lastIndex = 0

        const parent = node.parentNode
        if (!parent) continue

        let last = 0
        const frag = doc.createDocumentFragment()
        let m: RegExpExecArray | null
        while ((m = pattern.exec(text)) !== null) {
          if (m.index > last) {
            frag.appendChild(doc.createTextNode(text.slice(last, m.index)))
          }
          const rawUrl = m[0]
          const a = doc.createElement('a')
          a.href = normalizeHref(rawUrl)
          a.target = '_blank'
          a.rel = 'noopener noreferrer'
          a.textContent = rawUrl
          frag.appendChild(a)
          last = m.index + rawUrl.length
        }
        if (last < text.length) {
          frag.appendChild(doc.createTextNode(text.slice(last)))
        }
        if (frag.childNodes.length) {
          parent.replaceChild(frag, node)
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element
        const tag = el.tagName.toLowerCase()
        if (tag === 'script' || tag === 'style' || tag === 'textarea' || tag === 'pre' || tag === 'code') {
          continue
        }
        if (tag === 'a' && el.getAttribute('href')) {
          continue
        }
        walk(el)
      }
    }
  }

  walk(root)
  return root.innerHTML
}

/** URL을 앵커 태그로 래핑 */
export const wrapUrlWithAnchorTag = (text: string): string => {
  if (isEmpty(text)) return text;

  return text.replace(
    /<a\s+(?:[^>]*?\s+)?href=(['"])(.*?)\1[^>]*>(.*?)<\/a>|<img\s+[^>]*src=(['"])(.*?)\1[^>]*>|(\bhttps?:\/\/[^\s<]+)/g,
    (match, aQuote, aUrl, innerContent, imgQuote, imgUrl, plainUrl) => {
      // 이미 <a> 태그로 래핑된 URL인 경우에는 그대로 반환
      if (aUrl) {
        return match;
      }
      // 이미지 태그인 경우 imgurl로 새탭으로 열수 있게
      if (match.startsWith('<img')) {
        const pattern = /<img\b[^>]*?\bsrc=["']([^"']*)["'][^>]*>/g;
        const url = pattern.exec(match);
        if (url && url[1]) {
          const src = url[1];
          if (src.startsWith('data:')) {
            // data URL은 http(s) 링크가 아님 — <a>로 감싸면 뷰어에서 링크로 오인됨. 원본 img 유지(클릭은 뷰어에서 openImageModal)
            return match;
          } else {
            // 일반 URL이면 클릭 시 모달 열기
            const widthMatch = match.match(/width="(\d+)"/);
            const widthStyle = widthMatch ? `width:${widthMatch[1]}px;` : 'max-width:100%';
            return (
              "<img src='" +
              src +
              "' style='cursor:pointer; " +
              widthStyle +
              "' onclick='openImageModal(\"" +
              src +
              '\", "' +
              widthStyle +
              '")\'>'
            );
          }
        }
        return match;
      }
      // 나머지 경우에는 감싸서 반환
      return (
        '<a href="' + match + '" style="color:#0e62dd!important" target="_blank">' + match + '</a>'
      );
    },
  );
};

/** 이미지 미리보기 — UiModal 기반 (ImagePreviewModalHost + useImageModal) */
export { openImageModal } from '~/composables/useImageModal';

/** 객체를 URLSearchParams로 변환 */
export const getUrlSearchParams = (obj: Record<string, any>): URLSearchParams => {
  const params = new URLSearchParams();
  Object.keys(obj).forEach(key => {
    if (Array.isArray(obj[key])) {
      obj[key].forEach((item: any, index: number) => {
        Object.keys(item).forEach(subKey => {
          params.append(key + '[' + index + '].' + subKey, obj[key][index][subKey]);
        });
      });
    } else {
      params.append(key, nvl(obj[key], ''));
    }
  });
  return params;
};

/** HTML 이스케이프 맵 */
export const escapeHTMLMap: Record<string, string> = {
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '&amp;#160;': '',
};

/** HTML 언이스케이프 맵 */
export const unescapeHTMLMap: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
};

/** HTML 이스케이프 */
export const escapeHTML = (s: any): string => {
  if (isNotEmpty(s)) {
    s = s.toString();
    for (const k in escapeHTMLMap) {
      s = s.replaceAll(k, escapeHTMLMap[k]);
    }
  }
  return s;
};

/** HTML 언이스케이프 */
export const unescapeHTML = (s: any): any => {
  if (isNotEmpty(s)) {
    s = s.toString();
    for (const k in unescapeHTMLMap) {
      s = s.replaceAll(k, unescapeHTMLMap[k]);
    }
  }
  return s;
};

/** HTML 이스케이프하지 않음 */
export const nonEscapeHTML = (s: any): any => {
  if (Array.isArray(s)) {
    for (const idx in s) {
      if (typeof s[idx] === 'object') continue;
      let val = s[idx].toString();
      if (val) {
        if (val.endsWith('&#8203;')) {
          const lastIndex = val.lastIndexOf('&#8203;');
          s[idx] = val.substring(0, lastIndex) + val.substring(lastIndex + 7);
          return s;
        }
      }
      for (const k in unescapeHTMLMap) {
        s[idx] = val.replaceAll(k, unescapeHTMLMap[k]);
      }
    }
    return s;
  } else {
    s = s.toString();
    if (s) {
      if (s.endsWith('&#8203;')) {
        const lastIndex = s.lastIndexOf('&#8203;');
        s = s.substring(0, lastIndex) + s.substring(lastIndex + 7);
        return s;
      }
    }
    for (const k in unescapeHTMLMap) {
      s = s.replaceAll(k, unescapeHTMLMap[k]);
    }
    return s;
  }
};

/** 객체의 HTML 이스케이프 */
export const escapeHTMLObject = (o: any): any => {
  if (typeof o === 'object') {
    for (const k in o) {
      o[k] = escapeHTML(o[k]);
    }
  }
  return o;
};

/** 객체의 HTML 언이스케이프 */
export const nonEscapeHTMLObject = (o: any): any => {
  if (typeof o === 'object') {
    for (const k in o) {
      o[k] = nonEscapeHTML(o[k]);
    }
  }
  return o;
};

/** 배열의 객체들 HTML 언이스케이프 */
export const nonEscapeHTMLArrayByObject = (a: any): any => {
  if (typeof a === 'object') {
    for (const k in a) {
      a[k] = nonEscapeHTMLObject(a[k]);
    }
  }
  return a;
};

/** 객체 내 배열의 HTML 언이스케이프 */
export const nonEscapeHTMLArrayInObject = (a: any): any => {
  if (typeof a === 'object') {
    for (const k in a) {
      if (a[k] instanceof Array) {
        nonEscapeHTMLArrayByObject(a[k]);
      } else {
        a[k] = nonEscapeHTMLObject(a[k]);
      }
    }
  }
  return a;
};

/** URL을 링크로 변환 */
export const withLinks = (theText: string): string => {
  theText = escapeHTML(theText);
  const URLMatcher =
    /(?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#\/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#\/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#\/%=~_|$?!:,.]*\)|[A-Z0-9+&@#\/%=~_|$])/gim;
  if (isEmpty(theText)) return theText;
  return theText.replace(
    URLMatcher,
    match => `<a href="${match}" style="color:#0e62dd!important" target="_blank">${match}</a>`,
  );
};

/** date형 날짜를 string형 yyyymmdd 형태로 변환 */
export const formatDate = (date: string) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

/** 문자열의 바이트 길이 계산 */
export const lengthb = (str: string): number => new TextEncoder().encode(str || '').length;

/** 바이트 기준으로 문자열 자르기 */
export const lengthbCut = (str: string, maxBytes: number): string => {
  let bytes = 0;
  let result = '';

  for (const char of str) {
    const charByte = new TextEncoder().encode(char).length;
    if (bytes + charByte > maxBytes) break;
    bytes += charByte;
    result += char;
  }

  return result;
};

/** textarea 바이트 길이 표시 */
export const showBytes = (textRef: Ref<string>, byteCountRef: Ref<number>) => {
  watch(
    textRef,
    val => {
      byteCountRef.value = lengthb(val);
    },
    { immediate: true },
  );
};

/** 바이트 제한 설정 (초과 시 자동 자르기) */
export const setMaxLengthById = (textRef: Ref<string>, maxBytes: number) => {
  watch(textRef, val => {
    const newVal = lengthbCut(val, maxBytes);
    if (val !== newVal) textRef.value = newVal;
  });
};

/** 숫자 입력 제한 처리 함수 (소수점은 최대 1회, 음수 부호는 맨 앞만) */
export const inputNumeric = (event: Event): string => {
  const target = event.target as HTMLInputElement;
  let value = target.value;
  let neg = false;
  if (value.startsWith('-')) {
    neg = true;
    value = value.slice(1);
  }
  value = value.replace(/[^\d.]/g, '');
  const dotIdx = value.indexOf('.');
  if (dotIdx !== -1) {
    value = value.slice(0, dotIdx + 1) + value.slice(dotIdx + 1).replace(/\./g, '');
  }
  const result = neg ? `-${value}` : value;
  target.value = result;
  return result;
};

/** 문자열만으로 음수·허용 외 문자 제거 (소수점 최대 1회) — UiInput `unsigned`·부모 동기화용 */
export const sanitizeNumericUnsigned = (raw: string): string => {
  let value = String(raw ?? '').replace(/[^\d.]/g, '');
  const dotIdx = value.indexOf('.');
  if (dotIdx !== -1) {
    value = value.slice(0, dotIdx + 1) + value.slice(dotIdx + 1).replace(/\./g, '');
  }
  return value;
};

/** 숫자 입력 (소수점 최대 1회, 음수 부호 없음) — 가중치·점수·환산점수 등 */
export const inputNumericUnsigned = (event: Event): string => {
  const target = event.target as HTMLInputElement;
  const result = sanitizeNumericUnsigned(target.value);
  target.value = result;
  return result;
};

/** 양의 소수 입력 제한 (정수부·소수부 자릿수) */
export type UnsignedDecimalInputLimits = {
  integerDigits?: number;
  fractionalDigits?: number;
};

const DEFAULT_UNSIGNED_DECIMAL_LIMITS: Readonly<UnsignedDecimalInputLimits> = {
  integerDigits: 3,
  fractionalDigits: 3,
};

/** `maxlength` — 소수 허용 시 `정수 + '.' + 소수`, 미허용 시 정수만 */
export const getUnsignedDecimalMaxLength = (
  limits: UnsignedDecimalInputLimits = DEFAULT_UNSIGNED_DECIMAL_LIMITS,
): number => {
  const intMax = Math.max(0, limits.integerDigits ?? 3);
  const fracMax = Math.max(0, limits.fractionalDigits ?? 0);
  return fracMax > 0 ? intMax + 1 + fracMax : intMax;
};

/**
 * 양의 부호 없는 소수 입력 정제 — 정수부·소수부 자릿수 제한, 소수점 1회
 * @example sanitizeUnsignedDecimalInput('12.3456', { integerDigits: 3, fractionalDigits: 2 }) // '12.34'
 */
export const sanitizeUnsignedDecimalInput = (
  raw: string,
  limits: UnsignedDecimalInputLimits = DEFAULT_UNSIGNED_DECIMAL_LIMITS,
): string => {
  const intMax = Math.max(0, limits.integerDigits ?? 3);
  const fracMax = Math.max(0, limits.fractionalDigits ?? 0);
  const allowDecimal = fracMax > 0;

  let value = String(raw ?? '').replace(/[^\d.]/g, '');
  if (allowDecimal) {
    const dotIdx = value.indexOf('.');
    if (dotIdx !== -1) {
      value = value.slice(0, dotIdx + 1) + value.slice(dotIdx + 1).replace(/\./g, '');
    }
  } else {
    value = value.replace(/\./g, '');
  }

  let intPart = '';
  let fracPart = '';
  let seenDot = false;

  for (let i = 0; i < value.length; i++) {
    const c = value[i];
    if (c === '.' && allowDecimal) {
      if (!seenDot) {
        seenDot = true;
      }
      continue;
    }
    if (c === '.') {
      continue;
    }
    if (!seenDot) {
      if (intPart.length < intMax) {
        intPart += c;
      }
    } else if (fracPart.length < fracMax) {
      fracPart += c;
    }
  }

  if (seenDot && allowDecimal) {
    return fracPart.length > 0 ? `${intPart}.${fracPart}` : `${intPart}.`;
  }
  return intPart;
};

export const inputUnsignedDecimal = (
  event: Event,
  limits: UnsignedDecimalInputLimits = DEFAULT_UNSIGNED_DECIMAL_LIMITS,
): string => {
  const target = event.target as HTMLInputElement;
  const result = sanitizeUnsignedDecimalInput(target.value, limits);
  target.value = result;
  return result;
};

/** 0~9 정수만 허용 (소수점·음수 부호 제외). EditUiTable `integerOnly` 컬럼과 함께 사용 */
export const inputIntegerOnly = (event: Event): string => {
  const target = event.target as HTMLInputElement;
  const result = target.value.replace(/\D/g, '');
  target.value = result;
  return result;
};

/** 정수만 허용 + 선행 0 제거 (예: 010 → 10). 비율·점수 입력용 */
export const inputIntegerOnlyNormalized = (event: Event): string => {
  const target = event.target as HTMLInputElement;
  const digits = target.value.replace(/\D/g, '');
  const result = digits === '' ? '' : String(parseInt(digits, 10));
  target.value = result;
  return result;
};

/** 소수점 이하 0 제거 */
export const removePointZeros = (val: any, replaceIsNaN?: any, removeZero?: boolean): any => {
  if (removeZero === undefined) removeZero = true;
  if (isNaN(val)) {
    return replaceIsNaN;
  } else {
    if (removeZero) {
      return val
        .toString()
        .replace(/\.0+$/, '')
        .replace(/(\.\d*[1-9])0*/, '$1');
    } else {
      return val;
    }
  }
};

/**
 * 숫자 표시 포맷 (천단위 콤마 + 불필요한 소수점 0 제거) — 큰 수 정밀도 보존
 *
 * parseFloat/Number 로 값을 변환하지 않고 문자열 기반으로만 처리하므로
 * 16자리(2^53) 를 넘는 정수/소수도 원본 그대로 표시된다.
 * 목표값·실적값 등 표시용 포맷의 단일 진입점.
 *
 * @param val 원본 값 (문자열/숫자)
 * @param options.maxDecimals 소수점 표시 자리 제한 (정수부가 안전범위면 반올림, 아니면 자릿수 컷)
 * @param options.empty 빈 값(null/'')일 때 반환값 (기본 '')
 * @param options.invalid 숫자 형식이 아닐 때 반환값 (기본: 원본 문자열)
 */
export const formatNumberDisplay = (
  val: number | string | null | undefined,
  options: { maxDecimals?: number; empty?: string; invalid?: string } = {}
): string => {
  const { maxDecimals, empty = '', invalid } = options;
  if (val == null || val === '') return empty;
  const s = String(val).trim().replace(/,/g, '');
  if (s === '') return empty;
  // 숫자 형식이 아니면 원본(또는 invalid) 반환 (선행/후행 소수점 표기 ".5", "5." 도 허용)
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(s)) return invalid ?? String(val);
  let normalized = s;
  // 소수점 자리 제한
  if (maxDecimals != null && normalized.includes('.')) {
    const [intStr, decStr] = normalized.split('.');
    if (decStr.length > maxDecimals) {
      // 정수부가 안전 범위면 반올림(기존 동작 유지), 큰 수는 문자열 컷(정밀도 보존)
      normalized =
        intStr.replace('-', '').length <= 15
          ? Number(normalized).toFixed(maxDecimals)
          : `${intStr}.${decStr.slice(0, maxDecimals)}`;
    }
  }
  // 문자열 기반 소수점 0 제거 후 콤마 (parseFloat/Number 미사용)
  return addComma(removePointZeros(normalized));
};

/** 프로필 캐릭터 이미지 경로 생성 */
export const getProfileChrImg = (sex: string): string =>
  isEmpty(sex) ? '/images/icon-gnb-my.svg' : `/css/ui/img/profileChr-${sex}.svg`;

/** 사용자 프로필 이미지 경로 생성 */
/*export const getProfileImg = (user: string | any): string => {
  let imgPath = ''
  let userObj: any = null
  let userId = ''

  if (typeof user === 'string') {
    userId = user
    if (commonStore.gUserList && commonStore.gUserList.length > 0) {
      userObj = commonStore.gUserList.find(u => u.userId === userId)
    }
  } else if (typeof user === 'object' && user !== null) {
    userObj = user
    userId = user.userId
  }

  if (isEmpty(userObj)) {
    if (userId === 'system') {
      imgPath = '/images/common/sgate_bot_icon.png'
    } else {
      imgPath = getProfileChrImg('')
    }
  } else {
    const profileImg = nvl(userObj.profileImg, userObj.id)
    imgPath = `/images/profile/pics/${userObj.compId}/${profileImg}_thumb500.jpg?${new Date().getTime()}`
  }
  return imgPath
}*/

/** 값 포함 여부 확인 */
export const contains = (obj: any, checkVal: any): boolean => {
    if (obj == '' || obj == null || obj == undefined) {
        return false
    } else if (typeof obj == 'object') {
        return obj.includes(checkVal)
    } else if (typeof obj == 'string') {
        return obj == checkVal
    } else if (typeof obj == 'number') {
        return Number(obj) == Number(checkVal)
    } else {
        return false
    }
}

/** 날짜에서 일(day) 추출 */
export const getDatepickerDay = (date: string | Date): number =>
    new Date(date).getDate()

/** YYYY.MM 형식 날짜 유효성 검증 및 변환 */
export const validateDateFormat = (date: string | null | undefined): string | null => {
    if (!date) return null
    const datePattern = /^\d{4}\.(0[1-9]|1[0-2])$/
    return datePattern.test(date) ? date : null
}

// === 기존 useCommon 구조 유지 ===
export const useCommon = () => {
  return {
    addComma,
    removeComma,
    numeric,
    numericExt,
    isUndefined,
    isNull,
    isEmpty,
    isNotEmpty,
    hasStringLength,
    removeNull,
    removeEmpty,
    nvl,
    nl2br,
    checkEmail,
    createCookie,
    getCookie,
    deleteCookie,
    setCookie,
    makeBr,
    linkifyPlainTextUrlsInHtml,
    wrapUrlWithAnchorTag,
    openImageModal,
    getUrlSearchParams,
    escapeHTMLMap,
    unescapeHTMLMap,
    escapeHTML,
    unescapeHTML,
    nonEscapeHTML,
    escapeHTMLObject,
    nonEscapeHTMLObject,
    nonEscapeHTMLArrayByObject,
    nonEscapeHTMLArrayInObject,
    withLinks,
    formatDate,
    lengthb,
    lengthbCut,
    showBytes,
    setMaxLengthById,
    inputNumeric,
    inputNumericUnsigned,
    sanitizeNumericUnsigned,
    sanitizeUnsignedDecimalInput,
    getUnsignedDecimalMaxLength,
    inputUnsignedDecimal,
    inputIntegerOnly,
    inputIntegerOnlyNormalized,
    removePointZeros,
    getProfileChrImg,
    contains,
    getDatepickerDay,
    validateDateFormat,
  };
};
